import React, { useState, useEffect, useCallback, useRef } from "react";
import { FileText, Upload, Eye, EyeOff, Trash2, Download, History, X, RefreshCw, CheckSquare, Square } from "lucide-react";
import { Eyebrow, BASE } from "./e360Primitives";
import api from "../../lib/api";
import { toast } from "sonner";

const fmtBytes = (b) => {
  if (!b && b !== 0) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

function AccessLogModal({ docId, title, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/admin/portal/documents/${docId}/access-log`).then(({ data }) => setRows(data.access || [])).catch(() => setRows([]));
  }, [docId]);
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[80vh] overflow-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="doc-access-log-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[14px] font-semibold text-zinc-900">Access log — {title}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-black" data-testid="doc-access-log-close"><X size={18} /></button>
        </div>
        {rows === null ? <p className="text-sm text-zinc-400 py-6 text-center">Loading…</p>
          : rows.length === 0 ? <p className="text-[13px] text-zinc-500 py-6 text-center">No views yet.</p>
          : <table className="w-full text-[12px]"><tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-zinc-100">
                  <td className="py-2 pr-2 text-zinc-800">{r.email}</td>
                  <td className="py-2 px-2 text-zinc-500">{(r.action || "").replace(/_/g, " ").replace(/^\w/, (m) => m.toUpperCase())}</td>
                  <td className="py-2 pl-2 text-zinc-400 text-right whitespace-nowrap">{fmtDate(r.at)}</td>
                </tr>
              ))}
            </tbody></table>}
      </div>
    </div>
  );
}

export default function ClientDocuments({ clientId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [file, setFile] = useState(null);
  const [reqAck, setReqAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [logDoc, setLogDoc] = useState(null);
  const replaceRef = useRef(null);
  const replacingId = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/portal/engagement/${clientId}/documents`); setDocs(data.documents || []); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) { toast.error("Choose a file"); return; }
    if (!title.trim()) { toast.error("Enter a title"); return; }
    const fd = new FormData();
    fd.append("file", file); fd.append("title", title.trim()); fd.append("category", category.trim());
    fd.append("requires_ack", reqAck ? "true" : "false");
    setBusy(true);
    try {
      await api.post(`/admin/portal/engagement/${clientId}/documents`, fd);
      toast.success("Uploaded — publish it when ready");
      setTitle(""); setCategory(""); setFile(null); setReqAck(false);
      document.getElementById("doc-file-input").value = "";
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const togglePublish = async (d) => {
    try {
      const { data } = await api.patch(`/admin/portal/documents/${d.id}`, { published: !d.published });
      toast.success(!d.published ? `Published${data.notified ? ` · ${data.notified} client(s) notified` : ""}` : "Unpublished");
      load();
    } catch { toast.error("Could not update"); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}"? Clients will no longer see it.`)) return;
    try { await api.delete(`/admin/portal/documents/${d.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };
  const startReplace = (id) => { replacingId.current = id; replaceRef.current?.click(); };
  const onReplaceFile = async (e) => {
    const f = e.target.files?.[0]; const id = replacingId.current;
    e.target.value = "";
    if (!f || !id) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/admin/portal/documents/${id}/replace`, fd); toast.success("New version uploaded — publish it when ready"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Replace failed"); }
  };
  const toggleAck = async (d) => {
    try { await api.patch(`/admin/portal/documents/${d.id}`, { requires_ack: !d.requires_ack }); toast.success(!d.requires_ack ? "Acknowledgement now required" : "Acknowledgement no longer required"); load(); }
    catch { toast.error("Could not update"); }
  };

  return (
    <div className="border border-zinc-200 rounded-lg p-5 mt-6" data-testid="client-documents-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Eyebrow><span className="inline-flex items-center gap-1.5"><FileText size={13} /> Client documents</span></Eyebrow>
        {docs.length > 0 && (
          <a href={`${BASE}/api/admin/portal/engagement/${clientId}/evidence-export`} target="_blank" rel="noreferrer"
            data-testid="evidence-export-btn"
            className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.1em] uppercase border border-[var(--e360-primary,#0B5FFF)] text-[var(--e360-primary,#0B5FFF)] px-2.5 py-1.5 rounded hover:bg-[var(--e360-primary,#0B5FFF)] hover:text-white transition-colors">
            <Download size={13} /> Evidence pack
          </a>
        )}
      </div>
      <p className="text-[12px] text-zinc-500 mb-4">Upload deliverables for this engagement, then publish to make them visible in the client portal. Only granted clients for this engagement can see published documents. Every download is logged.</p>

      <div className="bg-zinc-50 border border-zinc-200 rounded-md p-4 mb-5 grid gap-2 sm:grid-cols-[1fr_1fr_auto] items-end">
        <div>
          <label className="text-[10px] uppercase tracking-[0.1em] text-zinc-400">Title</label>
          <input data-testid="doc-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Q3 Board Assurance Pack"
            className="w-full border border-zinc-300 rounded px-2 py-1.5 text-[13px] mt-0.5" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.1em] text-zinc-400">Category (optional)</label>
          <input data-testid="doc-category-input" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Board pack"
            className="w-full border border-zinc-300 rounded px-2 py-1.5 text-[13px] mt-0.5" />
        </div>
        <div className="flex gap-2">
          <input id="doc-file-input" data-testid="doc-file-input" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)}
            accept=".pdf,.docx,.pptx,.xlsx,.csv,.txt,.png,.jpg,.jpeg"
            className="text-[12px] max-w-[200px]" />
          <button data-testid="doc-upload-btn" disabled={busy} onClick={upload}
            className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.1em] uppercase bg-[var(--e360-primary,#0B5FFF)] text-white px-3 py-1.5 rounded disabled:opacity-40 shrink-0"><Upload size={13} /> {busy ? "Uploading…" : "Upload"}</button>
        </div>
        <label className="sm:col-span-3 flex items-center gap-2 text-[12px] text-zinc-600 cursor-pointer">
          <input type="checkbox" data-testid="doc-requires-ack-input" checked={reqAck} onChange={(e) => setReqAck(e.target.checked)} />
          Require the client to acknowledge receipt of this document
        </label>
      </div>

      {loading ? <p className="text-sm text-zinc-400 py-6 text-center">Loading…</p>
        : docs.length === 0 ? <p className="text-[13px] text-zinc-500 py-6 text-center">No documents uploaded for this engagement yet.</p>
        : <div className="space-y-2" data-testid="client-documents-list">
            {docs.map((d) => (
              <div key={d.id} data-testid={`admin-document-${d.id}`} className="flex items-center justify-between gap-3 border border-zinc-200 rounded-md px-3 py-2.5">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-semibold text-zinc-900 truncate">{d.title}</span>
                    {d.version > 1 && <span className="text-[10px] font-bold text-[var(--e360-primary,#0B5FFF)]" title={`Version ${d.version}`}>v{d.version}</span>}
                    <span className={`text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded ${d.published ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-zinc-100 text-zinc-500"}`}>{d.published ? "Published" : "Draft"}</span>
                    {d.requires_ack && <span className="text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200" title="Client must acknowledge receipt">Ack required</span>}
                  </div>
                  <div className="text-[11px] text-zinc-400 flex flex-wrap gap-x-2 mt-0.5">
                    {d.category && <span className="text-[var(--e360-primary,#0B5FFF)]">{d.category}</span>}
                    <span>{d.filename}</span><span>·</span><span>{fmtBytes(d.size)}</span>
                    {d.published_at && <><span>·</span><span>Published {fmtDate(d.published_at)}</span></>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <a href={`${BASE}/api/admin/portal/documents/${d.id}/download`} target="_blank" rel="noreferrer" title="Preview" className="p-1.5 text-zinc-500 hover:text-[var(--e360-primary,#0B5FFF)]" data-testid={`admin-document-preview-${d.id}`}><Download size={15} /></a>
                  <button onClick={() => startReplace(d.id)} title="Upload new version" className="p-1.5 text-zinc-500 hover:text-[var(--e360-primary,#0B5FFF)]" data-testid={`admin-document-replace-${d.id}`}><RefreshCw size={15} /></button>
                  <button onClick={() => setLogDoc(d)} title="Access log" className="p-1.5 text-zinc-500 hover:text-[var(--e360-primary,#0B5FFF)]" data-testid={`admin-document-log-${d.id}`}><History size={15} /></button>
                  <button onClick={() => toggleAck(d)} title={d.requires_ack ? "Acknowledgement required (click to turn off)" : "Require acknowledgement"} className={`p-1.5 hover:text-black ${d.requires_ack ? "text-amber-600" : "text-zinc-500"}`} data-testid={`admin-document-ack-toggle-${d.id}`}>{d.requires_ack ? <CheckSquare size={15} /> : <Square size={15} />}</button>
                  <button onClick={() => togglePublish(d)} title={d.published ? "Unpublish" : "Publish"} className="p-1.5 text-zinc-500 hover:text-black" data-testid={`admin-document-publish-${d.id}`}>{d.published ? <EyeOff size={15} /> : <Eye size={15} />}</button>
                  <button onClick={() => remove(d)} title="Delete" className="p-1.5 text-zinc-400 hover:text-red-600" data-testid={`admin-document-delete-${d.id}`}><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>}

      {logDoc && <AccessLogModal docId={logDoc.id} title={logDoc.title} onClose={() => setLogDoc(null)} />}
      <input ref={replaceRef} type="file" onChange={onReplaceFile} accept=".pdf,.docx,.pptx,.xlsx,.csv,.txt,.png,.jpg,.jpeg" className="hidden" data-testid="doc-replace-input" />
    </div>
  );
}
